import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getSesionEnVivo,
  subscribeToProgramaEstado,
  iniciarPrograma as svcIniciar,
  pausarPrograma as svcPausar,
  reanudarPrograma as svcReanudar,
  saltarSeccion as svcSaltar,
  seccionAnterior as svcAnterior,
  extenderSeccion as svcExtender,
  terminarPrograma as svcTerminar
} from '../lib/supabase'
import { alertaTermino } from '../lib/training/alertas'

export const useProgramaEnVivo = ({ sesionId, staffId }) => {
  const [sesion, setSesion] = useState(null)
  const [estado, setEstado] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [terminadoRecien, setTerminadoRecien] = useState(false)
  const unsubscribeRef = useRef(null)

  const load = useCallback(async () => {
    if (!sesionId) return
    setLoading(true)
    const { data, error: err } = await getSesionEnVivo(sesionId)
    if (err) {
      setError('No pudimos cargar la sesion')
    } else {
      setSesion(data)
      setEstado(data?.programa_estado_en_vivo || null)
    }
    setLoading(false)
  }, [sesionId])

  useEffect(() => {
    load()
    if (!sesionId) return
    unsubscribeRef.current = subscribeToProgramaEstado(sesionId, (newRow) => {
      setEstado(newRow.programa_estado_en_vivo || null)
      setSesion((prev) => prev ? { ...prev, ...newRow } : prev)
    })
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
      }
    }
  }, [sesionId, load])

  const programa = sesion?.asignacion?.programa
  const secciones = programa?.secciones || []
  const syncToken = sesion?.asignacion?.grupo_sync_token || null

  const iniciar = useCallback(async () => {
    if (!programa?.id) return
    const { data, error: err } = await svcIniciar({
      sesionId,
      programaId: programa.id,
      syncToken
    })
    if (err) setError(err.message)
    else setEstado(data)
  }, [programa?.id, sesionId, syncToken])

  const pausar = useCallback(async () => {
    if (!estado) return
    const { data, error: err } = await svcPausar({ sesionId, estadoActual: estado })
    if (err) setError(err.message)
    else setEstado(data)
  }, [estado, sesionId])

  const reanudar = useCallback(async () => {
    if (!estado) return
    const { data, error: err } = await svcReanudar({ sesionId, estadoActual: estado })
    if (err) setError(err.message)
    else setEstado(data)
  }, [estado, sesionId])

  const saltar = useCallback(async () => {
    if (!estado) return
    const { data, error: err, terminado } = await svcSaltar({
      sesionId,
      estadoActual: estado,
      totalSecciones: secciones.length,
      staffId
    })
    if (err) setError(err.message)
    else {
      setEstado(data)
      if (terminado) {
        setTerminadoRecien(true)
        alertaTermino()
      }
    }
  }, [estado, sesionId, secciones.length, staffId])

  const anterior = useCallback(async () => {
    if (!estado) return
    const { data, error: err } = await svcAnterior({ sesionId, estadoActual: estado })
    if (err) setError(err.message)
    else setEstado(data)
  }, [estado, sesionId])

  const extender = useCallback(async (segundos) => {
    if (!estado) return
    const { data, error: err } = await svcExtender({
      sesionId, estadoActual: estado, segundos
    })
    if (err) setError(err.message)
    else setEstado(data)
  }, [estado, sesionId])

  const terminar = useCallback(async () => {
    const { error: err } = await svcTerminar({ sesionId, staffId })
    if (err) setError(err.message)
    else {
      setTerminadoRecien(true)
      setEstado(null)
      alertaTermino()
      await load()
    }
  }, [sesionId, staffId, load])

  return {
    sesion,
    programa,
    secciones,
    estado,
    loading,
    error,
    terminadoRecien,
    iniciar,
    pausar,
    reanudar,
    saltar,
    anterior,
    extender,
    terminar,
    reload: load
  }
}
